import type { HttpError } from "./httpService";

const BACKEND_FACTORS_REQUESTS_PER_MINUTE = 600;
const DEFAULT_MAX_CONCURRENT = 8;
const DEFAULT_REQUESTS_PER_MINUTE =
  process.env.NODE_ENV === "test" ? 60000 : Math.floor(BACKEND_FACTORS_REQUESTS_PER_MINUTE * 0.8);
const DEFAULT_BURST_SIZE = process.env.NODE_ENV === "test" ? 1000 : 20;
const DEFAULT_RETRY_ATTEMPTS = 2;
const DEFAULT_RETRY_BASE_DELAY_MS = 1000;
const DEFAULT_MAX_RETRY_DELAY_MS = 15000;

interface FactorsRequestLimiterConfig {
  maxConcurrent: number;
  requestsPerMinute: number;
  burstSize: number;
  retryAttempts: number;
  retryBaseDelayMs: number;
  maxRetryDelayMs: number;
}

const defaultConfig: FactorsRequestLimiterConfig = {
  maxConcurrent: DEFAULT_MAX_CONCURRENT,
  requestsPerMinute: DEFAULT_REQUESTS_PER_MINUTE,
  burstSize: DEFAULT_BURST_SIZE,
  retryAttempts: DEFAULT_RETRY_ATTEMPTS,
  retryBaseDelayMs: DEFAULT_RETRY_BASE_DELAY_MS,
  maxRetryDelayMs: DEFAULT_MAX_RETRY_DELAY_MS,
};

class FactorsRequestLimiter {
  private config: FactorsRequestLimiterConfig = { ...defaultConfig };
  private active = 0;
  private waiters: Array<() => void> = [];
  private tokens = this.config.burstSize;
  private lastRefillAt = Date.now();
  private cooldownUntil = 0;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  async run<T>(operation: () => Promise<T>): Promise<T> {
    return this.runAttempt(operation, 0);
  }

  reset(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.active = 0;
    this.waiters = [];
    this.tokens = this.config.burstSize;
    this.lastRefillAt = Date.now();
    this.cooldownUntil = 0;
  }

  configureForTesting(config: Partial<FactorsRequestLimiterConfig>): void {
    this.config = { ...defaultConfig, ...config };
    this.reset();
  }

  private async runAttempt<T>(operation: () => Promise<T>, attempt: number): Promise<T> {
    await this.acquire();

    let rateLimitDelayMs: number | null = null;
    try {
      return await operation();
    } catch (error) {
      if (isRateLimitError(error) && attempt < this.config.retryAttempts) {
        rateLimitDelayMs = retryDelayMs(error, attempt, this.config);
      } else {
        throw error;
      }
    } finally {
      if (rateLimitDelayMs != null) {
        this.applyCooldown(rateLimitDelayMs);
      }
      this.release();
    }

    await sleep(rateLimitDelayMs!);
    return this.runAttempt(operation, attempt + 1);
  }

  private acquire(): Promise<void> {
    return new Promise((resolve) => {
      this.waiters.push(resolve);
      this.flush();
    });
  }

  private release(): void {
    this.active = Math.max(0, this.active - 1);
    this.flush();
  }

  private flush(): void {
    this.clearFlushTimer();
    this.refillTokens();

    while (this.waiters.length > 0 && this.active < this.config.maxConcurrent) {
      const delayMs = this.delayBeforeNextStart();
      if (delayMs > 0) {
        this.scheduleFlush(delayMs);
        return;
      }

      const waiter = this.waiters.shift();
      if (!waiter) return;
      this.tokens = Math.max(0, this.tokens - 1);
      this.active += 1;
      waiter();
      this.refillTokens();
    }
  }

  private refillTokens(): void {
    const now = Date.now();
    if (this.config.requestsPerMinute <= 0) {
      this.tokens = this.config.burstSize;
      this.lastRefillAt = now;
      return;
    }

    const elapsedMs = Math.max(0, now - this.lastRefillAt);
    const tokensToAdd = (elapsedMs / 60000) * this.config.requestsPerMinute;
    this.tokens = Math.min(this.config.burstSize, this.tokens + tokensToAdd);
    this.lastRefillAt = now;
  }

  private delayBeforeNextStart(): number {
    const now = Date.now();
    const cooldownDelay = Math.max(0, this.cooldownUntil - now);
    if (cooldownDelay > 0) return cooldownDelay;
    if (this.tokens >= 1) return 0;

    const tokensPerMs = this.config.requestsPerMinute / 60000;
    if (tokensPerMs <= 0) return 1000;
    return Math.ceil((1 - this.tokens) / tokensPerMs);
  }

  private applyCooldown(delayMs: number): void {
    this.cooldownUntil = Math.max(this.cooldownUntil, Date.now() + delayMs);
  }

  private scheduleFlush(delayMs: number): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  private clearFlushTimer(): void {
    if (!this.flushTimer) return;
    clearTimeout(this.flushTimer);
    this.flushTimer = null;
  }
}

const limiter = new FactorsRequestLimiter();

export function runWithFactorsRequestGuard<T>(operation: () => Promise<T>): Promise<T> {
  return limiter.run(operation);
}

export function _resetFactorsRequestGuardForTesting(): void {
  limiter.reset();
}

export function _configureFactorsRequestGuardForTesting(
  config: Partial<FactorsRequestLimiterConfig>
): void {
  limiter.configureForTesting(config);
}

function isRateLimitError(error: unknown): boolean {
  const httpError = error as HttpError;
  return httpError.status === 429 || httpError.response?.status === 429;
}

function retryDelayMs(
  error: unknown,
  attempt: number,
  config: FactorsRequestLimiterConfig
): number {
  const retryAfterMs = parseRetryAfterMs(error);
  if (retryAfterMs != null) {
    return Math.min(retryAfterMs, config.maxRetryDelayMs);
  }

  const exponentialDelay = config.retryBaseDelayMs * 2 ** attempt;
  return Math.min(exponentialDelay, config.maxRetryDelayMs);
}

function parseRetryAfterMs(error: unknown): number | null {
  const headers = (error as HttpError).response?.headers;
  const retryAfter = headers?.get("Retry-After");
  if (!retryAfter) return null;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const dateMs = new Date(retryAfter).getTime();
  if (!Number.isFinite(dateMs)) return null;
  return Math.max(0, dateMs - Date.now());
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
